import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// Command-palette switcher for task lists — the modal half of _list_switcher.
// Rows are server-rendered (the accessible set is already loaded for the header),
// so text filtering and chip filtering are local and instant; nothing is fetched
// except creating a new list.
//
// Mounted on the backdrop at body level, so the header trigger can't reach it with
// a data-action. It listens for clicks on [data-list-switcher-trigger] instead,
// which also means any number of triggers work without extra wiring.
export default class extends Controller {
    static targets = ["backdrop", "dialog", "input", "item", "group", "chip", "createRow", "createLabel", "empty", "hint", "renameForm", "renameInput"]

    static values = {
        createUrl: String,
        filter: String
    }

    connect() {
        this._scope = "all"
        this._activeIndex = -1

        this._onDocClick = (event) => {
            let trigger = event.target.closest("[data-list-switcher-trigger]")
            if (!trigger) return
            event.preventDefault()
            this.toggle()
        }
        this._onDocKeydown = (event) => {
            if (event.key === "Escape" && this._isOpen()) this.close()
        }

        document.addEventListener("click", this._onDocClick)
        document.addEventListener("keydown", this._onDocKeydown)
    }

    disconnect() {
        document.removeEventListener("click", this._onDocClick)
        document.removeEventListener("keydown", this._onDocKeydown)
        this.close()
    }

    toggle() {
        if (this._isOpen()) this.close()
        else this.open()
    }

    open() {
        this.element.classList.remove("hidden")
        this.element.setAttribute("aria-hidden", "false")
        document.body.classList.add("list-switcher-open")
        this._syncTriggers("true")

        // Always reopen clean: a stale query from last time would hide the list
        // you came here to find.
        if (this.hasInputTarget) {
            this.inputTarget.value = ""
            this.inputTarget.focus()
        }
        this._scope = "all"
        this._syncChips()
        this.filter()
        this._scrollCurrentIntoView()
    }

    close() {
        if (!this.element) return
        this.element.classList.add("hidden")
        this.element.setAttribute("aria-hidden", "true")
        document.body.classList.remove("list-switcher-open")
        this._syncTriggers("false")
        this._setActive(-1)
        this._endRename()
    }

    closeOnBackdrop(event) {
        if (event.target === this.element) this.close()
    }

    stopPropagation(event) {
        event.stopPropagation()
    }

    // The row is a real link, so navigation happens on its own; this just stops the
    // modal flashing back into view behind the page transition.
    choose() {
        this.close()
    }

    selectScope(event) {
        this._scope = event.currentTarget.dataset.scope || "all"
        this._syncChips()
        this.filter()
        if (this.hasInputTarget) this.inputTarget.focus()
    }

    filter() {
        // A rename in progress edits the current row in place; any re-filter
        // (open, a chip, typing a query) would unhide that row under the editor.
        this._endRename()
        let query = this._query()
        let visible = []

        this.itemTargets.forEach((row) => {
            let matchesText = query === "" || row.dataset.name.includes(query)
            let scopes = (row.dataset.scopes || "").split(" ")
            // "archived" is opt-in, not just another facet: an archived list must
            // stay out of All (and out of every other chip), or archiving would
            // not have cleaned the index at all. It appears only under its own
            // chip. The one exception is the archived list you are currently
            // viewing, which the server files under "mine" so the current row
            // still renders.
            let isArchived = scopes.includes("archived")
            let matchesScope = this._scope === "archived"
                ? isArchived
                : !isArchived && (this._scope === "all" || scopes.includes(this._scope))
            let match = matchesText && matchesScope
            row.hidden = !match
            if (match) visible.push(row)
        })

        // A group header with nothing under it reads as an empty section, so hide
        // the whole group once every row in it is filtered out.
        this.groupTargets.forEach((group) => {
            let anyVisible = Array.from(group.querySelectorAll("[data-list-switcher-target='item']")).some((row) => !row.hidden)
            group.hidden = !anyVisible
        })

        this._updateCreateRow(query, visible.length)
        if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", visible.length > 0)

        // Preselect the first hit so Enter opens it — but only while narrowing,
        // otherwise Enter on an untouched panel would jump somewhere arbitrary.
        this._visible = visible
        this._setActive(query === "" && this._scope === "all" ? -1 : (visible.length ? 0 : -1))
    }

    inputKeydown(event) {
        if (event.key === "ArrowDown") {
            event.preventDefault()
            this._move(1)
        } else if (event.key === "ArrowUp") {
            event.preventDefault()
            this._move(-1)
        } else if (event.key === "Enter") {
            event.preventDefault()
            let active = this._visible && this._visible[this._activeIndex]
            if (active) active.click()
            else if (this.hasCreateRowTarget && !this.createRowTarget.hidden) this.createFromFilter()
        } else if (event.key === "Escape") {
            // First Escape clears a query, second closes — the document handler
            // would otherwise close the panel on someone still refining a search.
            if (this.inputTarget.value !== "") {
                event.preventDefault()
                event.stopPropagation()
                this.inputTarget.value = ""
                this.filter()
            }
        }
    }

    createFromFilter() {
        let name = this._query({ raw: true })
        if (!name || !this.createUrlValue) return

        fetch(this.createUrlValue, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            credentials: "same-origin",
            body: JSON.stringify({ name, filter: this.filterValue || null })
        })
            .then((response) => response.json().then((data) => ({ response, data })))
            .then(({ response, data }) => {
                if (!response.ok || !data.ok) throw new Error(data.error || "Could not create list")
                window.location.assign(data.switch_url)
            })
            .catch(() => {})
    }

    // Family trees only (shared_family_trees/_tree_switcher): the current row
    // becomes its own editor. The form is a sibling of the row's link, not
    // inside it - an input inside an <a> is invalid and every click on it would
    // navigate.
    startRename() {
        if (!this.hasRenameFormTarget) return
        if (this.hasInputTarget) this.inputTarget.value = ""
        this._scope = "all"
        this._syncChips()
        this.filter()

        this._renamingRow = this.element.querySelector(".list-switcher-item.is-current")
        if (this._renamingRow) this._renamingRow.hidden = true
        this.renameFormTarget.hidden = false
        let input = this.renameInputTarget
        input.value = input.defaultValue
        input.focus()
        input.select()
    }

    cancelRename() {
        this._endRename()
        if (this.hasInputTarget) this.inputTarget.focus()
    }

    // Escape backs out of the rename, not the whole modal: stopping it here
    // keeps the document handler from closing the switcher as well.
    renameKeydown(event) {
        if (event.key !== "Escape") return
        event.preventDefault()
        event.stopPropagation()
        this.cancelRename()
    }

    _endRename() {
        if (!this.hasRenameFormTarget || this.renameFormTarget.hidden) return
        this.renameFormTarget.hidden = true
        if (this._renamingRow) this._renamingRow.hidden = false
        this._renamingRow = null
    }

    _move(delta) {
        let rows = this._visible || []
        if (!rows.length) return
        let next = this._activeIndex + delta
        if (next < 0) next = rows.length - 1
        if (next >= rows.length) next = 0
        this._setActive(next)
    }

    _setActive(index) {
        this._activeIndex = index
        let rows = this._visible || []
        rows.forEach((row, i) => row.classList.toggle("is-active", i === index))
        if (index >= 0 && rows[index]) rows[index].scrollIntoView({ block: "nearest" })
    }

    _updateCreateRow(query, visibleCount) {
        if (!this.hasCreateRowTarget) return
        let exact = this.itemTargets.some((row) => row.dataset.name === query)
        let show = query !== "" && !exact && this.createUrlValue
        this.createRowTarget.hidden = !show
        if (show && this.hasCreateLabelTarget) {
            this.createLabelTarget.textContent = `Create "${this._query({ raw: true })}"`
        }
        // The keyboard hint and the create button share the footer; swap rather
        // than stack, so the footer height never jumps.
        if (this.hasHintTarget) this.hintTarget.hidden = show
    }

    _scrollCurrentIntoView() {
        let current = this.element.querySelector(".list-switcher-item.is-current")
        if (current) current.scrollIntoView({ block: "nearest" })
    }

    _syncChips() {
        this.chipTargets.forEach((chip) => {
            let active = (chip.dataset.scope || "all") === this._scope
            chip.classList.toggle("is-active", active)
            chip.setAttribute("aria-pressed", active ? "true" : "false")
        })
    }

    _syncTriggers(expanded) {
        document.querySelectorAll("[data-list-switcher-trigger]").forEach((trigger) => {
            trigger.setAttribute("aria-expanded", expanded)
        })
    }

    _isOpen() {
        return !this.element.classList.contains("hidden")
    }

    _query({ raw = false } = {}) {
        let value = this.hasInputTarget ? this.inputTarget.value.trim() : ""
        return raw ? value : value.toLowerCase()
    }
}
